import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import VideoUploadComponent from '../components/VideoUploadComponent';
import ProcessingStatusComponent from '../components/ProcessingStatusComponent';
import ResultsViewerComponent from '../components/ResultsViewerComponent';
import AsgDashboardComponent from '../components/AsgDashboardComponent';
import { getTestSet } from '../api/videoApi';

export default function VideoProcessingPage() {
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [jobCompleted, setJobCompleted] = useState(false);
  const [isBenchmark, setIsBenchmark] = useState(false);
  const [testSetExists, setTestSetExists] = useState<boolean | null>(null);
  const [testSetInfo, setTestSetInfo] = useState<{ frame_count: number; pipeline_count: number; created_at: string } | null>(null);

  const handleJobStarted = (jobId: string, benchmark?: boolean) => {
    setCurrentJobId(jobId);
    setJobCompleted(false);
    setIsBenchmark(benchmark ?? false);
  };

  const handleJobComplete = (jobId: string) => {
    setJobCompleted(true);
  };

  const handleJobError = (jobId: string, error: string) => {
    console.error(`Job ${jobId} failed:`, error);
  };

  useEffect(() => {
    const checkTestSet = () => {
      getTestSet()
        .then((testSetData) => {
          if (testSetData) {
            setTestSetExists(true);
            let frameCount: number;
            let pipelineCount = 1;
            if ('pipelines' in testSetData && testSetData.pipelines) {
              const pipelines = Object.values(testSetData.pipelines);
              pipelineCount = pipelines.length;
              frameCount = Math.max(...pipelines.map(p => p.depth_maps?.length ?? 0), 0);
            } else {
              frameCount = 'frame_count' in testSetData ? testSetData.frame_count : testSetData.total_files;
            }
            setTestSetInfo({ frame_count: frameCount, pipeline_count: pipelineCount, created_at: testSetData.created_at });
          } else {
            setTestSetExists(false);
            setTestSetInfo(null);
          }
        })
        .catch(() => {
          setTestSetExists(null);
        });
    };
    checkTestSet();
    const interval = setInterval(checkTestSet, 30_000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Video Depth Processing
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Upload a video to process with Depth Anything 3 for pose-conditioned depth estimation
        and camera pose extraction.
      </Typography>

      {testSetExists === true && testSetInfo && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Test set available: {testSetInfo.frame_count} frames, {testSetInfo.pipeline_count} pipeline{testSetInfo.pipeline_count !== 1 ? 's' : ''} (saved {new Date(testSetInfo.created_at).toLocaleString()})
        </Alert>
      )}
      {testSetExists === false && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          No test set saved yet. Process a video and save results to create one.
        </Alert>
      )}

      {/* ASG Dashboard */}
      <AsgDashboardComponent />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {(!currentJobId || jobCompleted) && (
          <VideoUploadComponent onJobStarted={handleJobStarted} />
        )}

        {currentJobId && (
          <ProcessingStatusComponent
            jobId={currentJobId}
            isBenchmark={isBenchmark}
            onComplete={handleJobComplete}
            onError={handleJobError}
          />
        )}

        {currentJobId && jobCompleted && (
          <ResultsViewerComponent jobId={currentJobId} isBenchmark={isBenchmark} />
        )}
      </Box>

    </Container>
  );
}
